import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import type { TFunction } from 'i18next'
import type { Constant } from '@pbt/pbt-ui-components'
import { LanguageUtils, Utils } from '@pbt/pbt-ui-components'

import { ConsentStatusName } from '~/constants/previousMedicalRecord'
import { getConsentStatus } from '~/store/reducers/constants'

// --- Consent texts hook ---

export interface ConsentTexts {
  consentText: string | null
  descriptionText: string
  showConsentLine: boolean
}

/**
 * Resolves consent display texts from consentStatus hashId using ConsentStatus constants from Redux.
 * Used by useConsentTexts and by tests (getConsentTexts).
 */
export function getConsentTexts(
  consentStatusId: string | undefined,
  t: TFunction,
  nullDescription: string,
  consentStatusConstants: Constant[],
): ConsentTexts {
  const consentEntity = Utils.findById(consentStatusId, consentStatusConstants)
  const showConsentLine = consentStatusId != null
  const consentText = consentEntity
    ? consentEntity?.name === ConsentStatusName.GRANTED
      ? t('Common:PMR_CONSENT.CONSENT_YES')
      : consentEntity?.name === ConsentStatusName.DENIED
        ? t('Common:PMR_CONSENT.CONSENT_NO')
        : LanguageUtils.getTranslatedFieldName(consentEntity)
    : null

  const descriptionText =
    consentEntity?.name === ConsentStatusName.GRANTED
      ? t('Common:PMR_CONSENT.CONSENT_YES_DESCRIPTION')
      : consentEntity?.name === ConsentStatusName.DENIED
        ? t('Common:PMR_CONSENT.CONSENT_NO_DESCRIPTION')
        : nullDescription

  return {
    consentText,
    descriptionText,
    showConsentLine,
  }
}

/**
 * Hook: resolves consent display texts from consentStatus hashId.
 * Uses useTranslation and useSelector(getConsentStatus) internally.
 */
export function useConsentTexts(
  consentStatusId: string | undefined,
  nullDescription: string,
): ConsentTexts {
  const { t } = useTranslation(['Common', 'TimeTable'])
  const consentStatusConstants = useSelector(getConsentStatus)
  return getConsentTexts(
    consentStatusId,
    t,
    nullDescription,
    consentStatusConstants,
  )
}

// --- Manual contact id (UUID v4) ---

/**
 * RFC 4122 UUID v4 pattern (e.g. crypto.randomUUID()).
 * Manual-added contacts use this id; we omit id when sending to API.
 */
export const UUID_V4_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function isManualContactId(id: string | undefined): boolean {
  return id != null && UUID_V4_REGEX.test(id)
}
